const themeParameterMap = {
    COLOR_PRIMARY: "--primary-color",
    COLOR_HOVER: "--primary-hover",
    COLOR_DARK: "--primary-dark",
    COLOR_LIGHT: "--primary-light",
    COLOR_DISABLED: "--primary-disabled",
};

// 【#524】色以外でキャッシュ対象にするパラメータ。
// タブのタイトルは React 起動前の index.html が既定値を出すため、
// キャッシュしておかないと再訪時に一瞬 RecACE が見えてから切り替わる。
// PLUS_MENU_SUFFIX も同じ理由で入れる。無いと再訪時の先行描画で
// plus メニューが旧ラベル（RecACEplus設定 等）で一瞬出てから切り替わる。
const cachedTextParameterIds = ["SYSTEMTITLE", "PLUS_MENU_SUFFIX"];

const THEME_STORAGE_KEY_PREFIX = "recace.systemParameters.";

export const getThemeParameterType = (host = window.location.host) => {
    return host.split('.').slice(-3).join('.');
};

const getThemeStorageKey = (parameterType = getThemeParameterType()) => {
    return `${THEME_STORAGE_KEY_PREFIX}${parameterType}`;
};

/**
 * localStorage へ保存する対象を抜き出す。
 * 色（themeParameterMap）に加えて、【#524】で cachedTextParameterIds
 * （SYSTEMTITLE / PLUS_MENU_SUFFIX）も対象にしている。
 * 名前は既存の呼び出し元との互換のため据え置いている。
 */
export const extractThemeParameters = (systemParameters = {}) => {
    const ids = Object.keys(themeParameterMap).concat(cachedTextParameterIds);
    return ids.reduce((result, parameterId) => {
        if (systemParameters[parameterId]) {
            result[parameterId] = systemParameters[parameterId];
        }
        return result;
    }, {});
};

export const hasThemeParameters = (systemParameters = {}) => {
    // 先行描画してよいかは色だけで判定する。色が 1 つでもあれば、残りは
    // App.css の既定値へフォールバックできるため初期表示は止めない。
    // 【#524】キャッシュ対象に SYSTEMTITLE を足したが、これは判定に含めない。
    // タイトルだけがある状態で先行描画すると、色が既定のまま描かれてから切り替わる。
    return Object.keys(themeParameterMap).some((parameterId) => systemParameters[parameterId]);
};

/**
 * タブのタイトルを SYSTEMTITLE で上書きする【#524】
 *
 * ブランド名の正は hm99_system_parameters（function_id='TEXT' / SYSTEMTITLE）。
 * index.html のドメイン別分岐は React 起動前の控えなので、値が取れたら上書きする。
 */
export const applyDocumentTitle = (systemParameters = {}) => {
    if (typeof document === "undefined" || !systemParameters.SYSTEMTITLE) {
        return;
    }
    document.title = systemParameters.SYSTEMTITLE;
    // 【#524】index.html の window.onload はこの後に発火しうる。
    // フラグを見て控えの上書きを止めさせる（打ち消されると DB 起点にならない）。
    if (typeof window !== "undefined") {
        window.__systemTitleFromDb = true;
    }
};

export const applyThemeColors = (systemParameters = {}) => {
    if (typeof document === "undefined") {
        return;
    }

    Object.entries(themeParameterMap).forEach(([parameterId, cssVariable]) => {
        if (!systemParameters[parameterId]) {
            return;
        }
        document.documentElement.style.setProperty(cssVariable, systemParameters[parameterId]);
    });
};

export const readCachedSystemParameters = (parameterType = getThemeParameterType()) => {
    if (typeof window === "undefined") {
        return {};
    }

    try {
        const cachedTheme = window.localStorage.getItem(getThemeStorageKey(parameterType));
        if (!cachedTheme) {
            return {};
        }
        return JSON.parse(cachedTheme);
    } catch (err) {
        console.warn("システムパラメータのキャッシュ読み込みに失敗しました。", err);
        return {};
    }
};

export const writeCachedSystemParameters = (systemParameters = {}, parameterType = getThemeParameterType()) => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        const themeParameters = extractThemeParameters(systemParameters);
        window.localStorage.setItem(getThemeStorageKey(parameterType), JSON.stringify(themeParameters));
    } catch (err) {
        console.warn("システムパラメータのキャッシュ保存に失敗しました。", err);
    }
};

export const bootstrapThemeColors = () => {
    const cachedSystemParameters = readCachedSystemParameters();
    applyThemeColors(cachedSystemParameters);
    // 【#524】再訪時のちらつきを抑えるため、タイトルもキャッシュから当てる。
    applyDocumentTitle(cachedSystemParameters);
    return cachedSystemParameters;
};
